import type { ComputedRef, Ref } from 'vue'
import { computed, toRef } from 'vue'
import type {
    GemType,
    GetReadyToGoRingQuery,
    ReadyToGoRingVariationsFragment,
    RingSizeInput,
} from '@/graphql/api'
import { useVariationIndex } from './useVariationIndex'
import { assertNever } from '@/helpers'
import { useI18n } from 'vue-i18n'
import { useNumberFormatter } from '@/composables/useNumberFormatter'
import {
    type RingRecommendedSizeOrSizeValue,
    useRingSize,
} from '@/domain/product/ring/useRingSize'
import type { SelectOption } from '@/components/ui/inputs/th-select.ts'

interface ReadyToGoRingProperties {
    ring: Readonly<
        Ref<GetReadyToGoRingQuery['catalogueReadyToGoRing'] | undefined>
    >
    weightIndex: ComputedRef<string>
    weightOptions: ComputedRef<SelectOption<string>[]>
    gemTypeIndex: ComputedRef<string>
    gemTypeOptions: ComputedRef<SelectOption<string>[]>

    handleRingSizeNotSelected(): RingSizeInput | null
    ringSizeError: Ref<string>
    ringSizeId: Ref<RingRecommendedSizeOrSizeValue | null>
    ringSizeOptions: ComputedRef<SelectOption<RingRecommendedSizeOrSizeValue>[]>
}

export function useReadyToGoRingProperties(
    data: Ref<GetReadyToGoRingQuery | null | undefined>
): ReadyToGoRingProperties {
    const formatNumber = useNumberFormatter(2, 2)
    const ring = toRef(() => data.value?.catalogueReadyToGoRing)

    const gemTypeVariations = computed<ReadyToGoRingVariationsFragment[]>(
        () =>
            ring.value?.variations
                .filter((variation) => variation.carat === ring.value?.carat)
                .sort((variation) =>
                    variation.gemType === 'NATURAL_DIAMOND' ? -1 : 1
                ) || []
    )

    const { t } = useI18n()

    const translateGemType = (gemType: GemType): string => {
        switch (gemType) {
            case 'LAB_GROWN_DIAMOND':
                return t('catalogue.gemType.lab_grown')
            case 'NATURAL_DIAMOND':
                return t('catalogue.gemType.natural_diamond')
            default:
                return assertNever(gemType, gemType)
        }
    }

    const gemTypeOptions = computed<SelectOption<string>[]>(() =>
        gemTypeVariations.value.map((variation, index) => ({
            value: String(index),
            label: translateGemType(variation.gemType),
        }))
    )

    const gemTypeIndex = useVariationIndex(gemTypeVariations, ring)

    const diamondWeightVariations = computed<ReadyToGoRingVariationsFragment[]>(
        () =>
            ring.value?.variations
                .filter(
                    (variation) => variation.gemType === ring.value?.gemType
                )
                .sort((a, b) => (a.carat || 0) - (b.carat || 0)) || []
    )

    const weightIndex = useVariationIndex(diamondWeightVariations, ring)

    const weightOptions = computed<SelectOption<string>[]>(() =>
        diamondWeightVariations.value.map((variation, index) => ({
            value: String(index),
            label: `${formatNumber(variation.carat || 0)}ct`,
        }))
    )

    const {
        handleRingSizeNotSelected,
        ringSizeError,
        ringSizeId,
        ringSizeOptions,
    } = useRingSize(toRef(() => ring.value?.sizes))

    return {
        ring,
        weightIndex,
        weightOptions,
        gemTypeIndex,
        gemTypeOptions,
        handleRingSizeNotSelected,
        ringSizeError,
        ringSizeId,
        ringSizeOptions,
    }
}
